import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import {
  FilterFn,
  ColumnFiltersState,
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table'
import clsx from 'clsx'
import threeDots from '../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import React, { useEffect, useRef, useState } from 'react'
import { Table, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toAbsoluteUrl, KTSVG } from '../../../../../_metronic/helpers'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../../widgets/components/NoDataPage'
import { useIntl } from 'react-intl'
import { SyncedFileFilter } from './SyncedFileFilter'
import { useDateFormat } from '../../../../hooks/useDateFormat'
import { useTimeZone } from '../../../../hooks/useTimeZone'
import { useAuth } from '../../../auth'
import { exportSyncedLogFiles, getAllSyncedLogFiles } from '../../core/_requests'
import { getFileType } from '../../../../core/_util'
import { RecordSync, storageIcons } from '../../core/_constants'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import { useInvestorDatabase } from '../../../investor-database/core/InvestorContext'
import ThreadTitle from '../../../AI-banker/components/shortenString'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

const SyncedLogDataTable = () => {
  const { formatMessage } = useIntl()
  const [globalFilter, setGlobalFilter] = useState('')
  const [, setClear] = useState(true) //eslint-disable-line
  const [searchAndFilter, setSearchAndFilter] = useState(false) //eslint-disable-line
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false) //eslint-disable-line
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [showKebabMenu, setShowKebabMenu] = useState(false)
  const [modalContainer, setModalContainer] = useState<any>()
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const [fileNameSearch, setFileNameSearch] = useState<string>('')
  const [sourceNameSearch, setSourceNameSearch] = useState<string[]>([])
  const [syncedStatusSearch, setSyncedStatusSearch] = useState<string>('')
  const [maxSize, setMaxSize] = useState<any>()
  const [minSize, setMinSize] = useState<any>()
  const [uploadedAfter, setUploadedAfter] = useState<string>('')
  const [uploadedBefore, setUploadedBefore] = useState<string>('')
  const [uploadedBy, setUploadedBy] = useState<any>('')
  const { companyId } = useAuth()
  const [allFiles, setAllFiles] = useState<any[]>([])
  const { getTimeZoneValue } = useTimeZone()
  const { getDateValue } = useDateFormat()
  const [isLoading, setIsLoading] = useState(false)
  const [isExportLoading, setIsExportLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const { setBlockOutsideClick } = useInvestorDatabase()
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }
  const getAllFilteredFiles = async () => {
    try {
      setIsLoading(true)

      const {
        data: { success, data },
      } = await getAllSyncedLogFiles(
        +companyId!,
        pagination.currentPage,
        pagination.pageSize,
        globalFilter || fileNameSearch,
        syncedStatusSearch,
        sourceNameSearch,
        +maxSize!,
        +minSize!,
        uploadedAfter,
        uploadedBefore,
        uploadedBy
      )
      if (success) {
        setAllFiles(data.data)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const exportToCsv = async () => {
    if (!companyId) return
    setIsExportLoading(true)
    try {
      const {
        data: { success, data: values },
      } = await exportSyncedLogFiles(
        +companyId!,
        globalFilter || fileNameSearch,
        syncedStatusSearch,
        sourceNameSearch,
        +maxSize!,
        +minSize!,
        uploadedAfter,
        uploadedBefore,
        uploadedBy
      )
      if (success) {
        toast.success(formatMessage({ id: 'Synced Files logs exported successfully' }))
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Synced Files Logs.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsExportLoading(false)
    }
  }

  useEffect(() => {
    getAllFilteredFiles()
    // eslint-disable-next-line
  }, [
    pagination.currentPage,
    pagination.pageSize,
    companyId,
    sourceNameSearch,
    syncedStatusSearch,
    maxSize,
    minSize,
    uploadedAfter,
    uploadedBefore,
    uploadedBy,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const parseFileSize = (sizeStr: string): number => {
    if (!sizeStr) return 0

    const match = sizeStr
      .trim()
      .toLowerCase()
      .match(/^([\d.]+)\s*(kb|mb|gb|b)?$/)
    if (!match) return 0

    const value = parseFloat(match[1])
    const unit = match[2]

    switch (unit) {
      case 'b':
        return value
      case 'kb':
        return value * 1024
      case 'mb':
        return value * 1024 * 1024
      case 'gb':
        return value * 1024 * 1024 * 1024
      default:
        return value
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorFn: (row) => row.fileVersion?.fileName || '',
        id: 'fileName',
        header: 'File Name',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2 '>
              <img
                src={getFileType(info.getValue())}
                alt={info.getValue()}
                width={24}
                height={24}
                className='mb-md-0 mb-2'
              />
              <div className='cursor-pointer'>
                <h5 className='m-0 fw-normal font-size-13'>{info.getValue()}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.fileVersion?.fileSize || '0 MB',
        id: 'fileSize',
        header: 'File Size',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2 '>
              <div className='cursor-pointer'>
                <h5 className='m-0 fw-normal font-size-13'>{info.getValue()}</h5>
              </div>
            </div>
          )
        },
        sortingFn: (rowA, rowB, columnId) => {
          const sizeA = parseFileSize(rowA.getValue(columnId))
          const sizeB = parseFileSize(rowB.getValue(columnId))
          return sizeA - sizeB
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.source?.storageName || '',
        id: 'sourceName',
        header: 'Storage Name',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2 '>
              <img
                src={toAbsoluteUrl(
                  `/media/icons/RecordsKeeperAI/${storageIcons[info.row.original?.source?.cloudType as RecordSync]
                  }.svg`
                )}
                alt={info.row.original?.source?.storageName}
                height={20}
                width={20}
              />
              <div style={{ maxWidth: '250px' }}>
                <p className='m-0 fw-normal font-size-13'>
                  <ThreadTitle title={info.row.original?.source?.storageName} />
                </p>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.fileVersion?.updatedAt || '',
        id: 'syncedOn',
        header: 'Synced On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <div className=''>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.fileVersion?.syncedStatus?.toLowerCase() || '',
        id: 'syncedStatus',
        header: 'Status',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2 '>
              <div
                className={`badge p-3 ${info.row?.original?.fileVersion?.syncedStatus.toLowerCase() === 'failed'
                  ? 'badge-light-danger'
                  : info.row?.original?.fileVersion?.syncedStatus?.toLowerCase() ===
                    'processed' ||
                    info.row?.original?.fileVersion?.syncedStatus?.toLowerCase() === 'synced'
                    ? 'badge-light-success'
                    : 'badge-light-info'
                  }`}
              >
                <p className='m-0 fw-normal font-size-13'>
                  {info.row.original?.fileVersion?.syncedStatus}
                </p>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.fileVersion?.processError || '',
        id: 'failureReason',
        header: 'Failure Reason',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2 justify-content-end'>
              <p className='m-0 fw-normal font-size-13'>
                {info.row.original?.fileVersion?.processError || '-'}
              </p>
            </div>
          )
        },
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const table = useReactTable({
    data: allFiles as any,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    // globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  return (
    <>
      <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center gap-2 mb-4'>
        <h1 className='font-size-16 mb-0'>{formatMessage({ id: 'Synced Files log' })}</h1>
        <div className='d-flex gap-md-2 gap-2'>
          <form className='align-items-center w-100 w-md-auto d-flex'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              value={globalFilter}
              onChange={(value) => {
                setGlobalFilter(String(value))
              }}
              placeholder='Search'
            />
          </form>
          {/* Filter Btn  */}
          <div className={`h-100  d-flex rounded ${isFilterApplied ? 'bg-success' : 'bg-white'}`}>
            <button
              className={clsx(
                'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
              )}
              id='fc-toogle'
              title='Sort and Filter'
              data-bs-toggle='tooltip'
              data-bs-placement='left'
              data-bs-dismiss='click'
              data-bs-trigger='hover'
              onClick={() => {
                setSearchAndFilter(true)
                setBlockOutsideClick(true)
              }}
            >
              <KTSVG
                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                className={`svg-icon-6 me-1 ${isFilterApplied ? 'svg-icon-white' : 'svg-icon-muted'
                  }`}
              />
              <span
                className={`d-none d-md-block ${isFilterApplied ? 'text-white' : 'text-muted'}`}
              >
                {formatMessage({ id: 'Filter' })}
                {isFilterApplied ? '*' : ''}
              </span>
              {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
            </button>
          </div>
          <div className='position-relative align-self-top height-36'>
            <div
              className='p-3 cursor-pointer bg-white rounded-2 d-flex justify-content-center align-items-center height-36'
              onClick={() => {
                setShowKebabMenu(!showKebabMenu)
              }}
            >
              <img src={threeDots} alt='' width={16} height={16} />
            </div>
            {showKebabMenu && (
              <div
                className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                style={{ top: '40px', right: 0 }}
                ref={dropdownRef}
              >
                <div className='rounded p-2 dropdown-items w-100 bg-hover-light'>
                  <button
                    type='button'
                    className='m-0 border-0 bg-transparent text-dark font-size-12 bg-hover-muted'
                    disabled={isExportLoading}
                    onClick={() => {
                      setShowKebabMenu(false)
                      exportToCsv()
                    }}
                  >
                    {isExportLoading
                      ? formatMessage({ id: 'Loading...' })
                      : formatMessage({ id: 'Export to CSV' })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && table.getRowModel().rows.length === 0 ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header, index) =>
                    index === 5 ? (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className='min-w-120px pb-6 pt-4 px-4 text-end'
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ) : (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className='min-w-120px pb-6 pt-4 px-4'
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    )
                  )}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr className='border-bottom' key={row.id}>
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        console.log(cell.getValue())

                        return (
                          <>
                            {cellIndex === 0 ? (
                              <td
                                key={cell.id}
                                className='pt-5 px-4 border-0'
                                style={{ minWidth: '200px', maxWidth: '300px' }}
                              >
                                <div className='d-flex align-items-center gap-2'>
                                  <span ref={(node) => setModalContainer(node)}>
                                    <OverlayTrigger
                                      placement='bottom'
                                      overlay={
                                        <Tooltip id='tooltip'>
                                          <p className='m-0'>{cell.getValue() as string}</p>
                                        </Tooltip>
                                      }
                                      container={modalContainer}
                                    >
                                      <h5 className='m-0 fw-normal font-size-13'>
                                        {(cell.getValue() as string).length > 20
                                          ? `${(cell.getValue() as string).substring(0, 30)}...`
                                          : (cell.getValue() as string)}
                                      </h5>
                                    </OverlayTrigger>
                                  </span>
                                </div>
                              </td>
                            ) :
                              cellIndex === 1 ? (
                                <td
                                  key={cell.id}
                                  className='pt-5 px-4 border-0'
                                  style={{ minWidth: '120px', maxWidth: '300px' }}
                                >
                                  <div className='d-flex align-items-center gap-2 text-wrap'>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </td>
                              ) : cellIndex === 4 ? (
                                <td key={cell.id} className='pt-3 px-4 border-0'>
                                  <div
                                    className={`d-flex justify-content-start align-items-center gap-2 ps-0 badge bg-opacity-25 text-wrap`}
                                    style={{ width: '120px', maxWidth: '300px' }}
                                  >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </td>
                              ) : cellIndex === 5 ? (
                                <td
                                  key={cell.id}
                                  className={`pt-3 px-4 border-0 text-end text-wrap`}
                                  style={{ minWidth: '150px', maxWidth: '300px' }}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              ) : (
                                <td
                                  key={cell.id}
                                  className={`pt-4 px-4 border-0 text-wrap`}
                                  style={{ minWidth: '150px', maxWidth: '300px' }}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              )}
                          </>
                        )
                      })}
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>
      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
        {allFiles?.length > 0 && (
          <div className='w-100 d-flex  flex-md-row flex-column justify-content-md-between align-items-center px-0 mt-8'>
            <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
              <select
                aria-label='Default select example'
                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark`}
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.nextPage && pagination.currentPage === 1
                      ? allFiles.length
                      : pagination.nextPage
                        ? pagination.pageSize * pagination.currentPage
                        : pagination.totalCount
                    } of ${pagination.totalCount} records`,
                })}
              </span>
            </div>

            <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {(() => {
                const items = []
                const { currentPage, totalPages } = pagination
                const pageNeighbours = 1 // number of pages to show on each side of current

                const startPage = Math.max(2, currentPage - pageNeighbours)
                const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours)

                // Always show first page
                items.push(
                  <Pagination.Item
                    key={1}
                    active={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>
                )

                // Show left ellipsis if needed
                if (startPage > 2) {
                  items.push(<Pagination.Ellipsis key='start-ellipsis' disabled />)
                }

                // Show middle page numbers
                for (let i = startPage; i <= endPage; i++) {
                  items.push(
                    <Pagination.Item
                      key={i}
                      active={i === currentPage}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  )
                }

                // Show right ellipsis if needed
                if (endPage < totalPages - 1) {
                  items.push(<Pagination.Ellipsis key='end-ellipsis' disabled />)
                }

                // Always show last page if totalPages > 1
                if (totalPages > 1) {
                  items.push(
                    <Pagination.Item
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  )
                }

                return items
              })()}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>
          </div>
        )}
      </div>
      <SyncedFileFilter
        filter={searchAndFilter}
        setFilter={setSearchAndFilter}
        setClear={setClear}
        setFileNameSearch={setFileNameSearch}
        setSourceNameSearch={setSourceNameSearch}
        setSyncedStatusSearch={setSyncedStatusSearch}
        setMaxSize={setMaxSize}
        setMinSize={setMinSize}
        setUploadedAfter={setUploadedAfter}
        setUploadedBefore={setUploadedBefore}
        setUploadedBy={setUploadedBy}
        setIsFilterApplied={setIsFilterApplied}
      />
    </>
  )
}

export default SyncedLogDataTable

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
